import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { generateClient } from 'aws-amplify/api';
import { GET_MY_TEAMS } from '../graphql/queries';
import { SET_USER_DEFAULT_TEAM } from '../graphql/mutations';

const client = generateClient();

const LOCAL_STORAGE_KEY = 'scanner_active_team_id';

export interface Team {
  teamId: string;
  name: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  members?: {
    userId: string;
    role: string;
    email?: string;
  }[];
}

interface TeamContextValue {
  teams: Team[];
  activeTeam: Team | null;
  activeTeamId: string | null;
  isLoading: boolean;
  error: string | null;
  hasMultipleTeams: boolean;
  setActiveTeam: (teamId: string | null) => Promise<void>;
  refreshTeams: () => Promise<void>;
}

const TeamContext = createContext<TeamContextValue | undefined>(undefined);

export function useTeamContext(): TeamContextValue {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeamContext must be used within a TeamContextProvider');
  }
  return context;
}

interface TeamContextProviderProps {
  children: ReactNode;
}

export function TeamContextProvider({ children }: TeamContextProviderProps) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [activeTeamId, setActiveTeamId] = useState<string | null>(() => {
    // Load from localStorage on initial mount
    if (typeof window !== 'undefined') {
      return localStorage.getItem(LOCAL_STORAGE_KEY);
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTeams = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await client.graphql({
        query: GET_MY_TEAMS,
      });

      const fetchedTeams = (response as { data: { getMyTeams: { items: Team[] } } }).data?.getMyTeams?.items || [];
      setTeams(fetchedTeams);

      // If active team is set but not in the list, clear it
      if (activeTeamId && !fetchedTeams.find(t => t.teamId === activeTeamId)) {
        setActiveTeamId(null);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }

      // If no active team but there's only one team, auto-select it
      if (!activeTeamId && fetchedTeams.length === 1) {
        const onlyTeamId = fetchedTeams[0].teamId;
        setActiveTeamId(onlyTeamId);
        localStorage.setItem(LOCAL_STORAGE_KEY, onlyTeamId);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams');
    } finally {
      setIsLoading(false);
    }
  }, [activeTeamId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const setActiveTeam = useCallback(async (teamId: string | null) => {
    // Update local state immediately for responsive UI
    setActiveTeamId(teamId);

    // Persist to localStorage
    if (teamId) {
      localStorage.setItem(LOCAL_STORAGE_KEY, teamId);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }

    // Also update user's default team on the server
    try {
      await client.graphql({
        query: SET_USER_DEFAULT_TEAM,
        variables: { teamId },
      });
    } catch (err) {
      console.error('Error updating default team:', err);
      // Don't throw - local state is already updated
    }
  }, []);

  const activeTeam = activeTeamId ? teams.find(t => t.teamId === activeTeamId) || null : null;
  const hasMultipleTeams = teams.length > 1;

  const value: TeamContextValue = {
    teams,
    activeTeam,
    activeTeamId,
    isLoading,
    error,
    hasMultipleTeams,
    setActiveTeam,
    refreshTeams: fetchTeams,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

// Export context for direct access if needed
export { TeamContext };
